import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.firstName, formData.lastName);
      toast.success('Account created successfully!');
      navigate('/apply');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] flex flex-col lg:flex-row relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-violet-600 rounded-full filter blur-[200px] opacity-10"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-cyan-600 rounded-full filter blur-[200px] opacity-10"></div>
      </div>
      
      {/* Left Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/80 via-[#12121A] to-cyan-900/50">
          <img
            src="https://images.pexels.com/photos/6196224/pexels-photo-6196224.jpeg"
            alt="Field technician ready for work"
            className="w-full h-full object-cover opacity-40 mix-blend-overlay"
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              <span className="silver-text">Start Your</span>{' '}
              <span className="gradient-text">Journey</span>
            </h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-md">
              Create an account to begin your application with Mighty Link
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 relative z-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block mb-6 sm:mb-8">
            <Logo />
          </Link>

          <div className="animate-fade-in-up">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              Join Our Team
            </h1>
            <p className="text-slate-500 mb-6 sm:mb-8 text-sm sm:text-base">
              Create an account to start your application
            </p>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-slate-400">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="h-11 sm:h-12 bg-[#12121A] border-slate-700 text-white placeholder:text-slate-600 focus:border-violet-500 focus:ring-violet-500/20"
                    data-testid="register-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-slate-400">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="h-11 sm:h-12 bg-[#12121A] border-slate-700 text-white placeholder:text-slate-600 focus:border-violet-500 focus:ring-violet-500/20"
                    data-testid="register-lastname"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-400">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-11 sm:h-12 bg-[#12121A] border-slate-700 text-white placeholder:text-slate-600 focus:border-violet-500 focus:ring-violet-500/20"
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-400">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                    className="h-11 sm:h-12 bg-[#12121A] border-slate-700 text-white placeholder:text-slate-600 pr-12 focus:border-violet-500 focus:ring-violet-500/20"
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-400">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="h-11 sm:h-12 bg-[#12121A] border-slate-700 text-white placeholder:text-slate-600 focus:border-violet-500 focus:ring-violet-500/20"
                  data-testid="register-confirm-password"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 sm:h-12 btn-gradient text-white border-0"
                data-testid="register-submit"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="ml-2" size={18} />
                  </>
                )}
              </Button>
            </form>

            <p className="mt-6 sm:mt-8 text-center text-slate-500 text-sm sm:text-base">
              Already have an account?{' '}
              <Link to="/login" className="text-violet-400 hover:text-violet-300 font-medium">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
